import React, { ChangeEvent, KeyboardEvent, useEffect, useState } from 'react';
import classNames from 'classnames';
import Hint from 'components/styleguide/hint/Hint';
import { useAutoSave } from 'components/styleguide/hooks/useAutoSave';

const styles = require('./Textarea.module.scss');

const randomId = () => `textarea-${Math.random().toString(36).slice(2)}`;

export type TextareaContext = 'neutral' | 'talent' | 'company';

export interface TextareaProps {
  autoSaveUrl?: string;
  className?: string;
  context?: TextareaContext;
  defaultValue?: string;
  disabled?: boolean;
  error?: string;
  hint?: string;
  id?: string;
  label?: string;
  maxLength?: number;
  name?: string;
  onChange?: (event: ChangeEvent<HTMLTextAreaElement>) => void;
  onKeyDown?: (event: KeyboardEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  required?: boolean;
  rows?: number;
  showCharacterCount?: boolean;
  textareaRef?: React.Ref<HTMLTextAreaElement>;
}

const Textarea: React.FC<TextareaProps> = ({
  autoSaveUrl,
  className,
  context = 'neutral',
  defaultValue,
  disabled,
  error,
  hint,
  id,
  label,
  maxLength,
  name,
  onChange,
  onKeyDown,
  placeholder,
  required,
  rows,
  showCharacterCount,
  textareaRef,
}) => {
  const { autoSaveError, save } = useAutoSave({ url: autoSaveUrl || '', name: name || '' });
  const [length, setLength] = useState(defaultValue?.length || 0);
  const [displayError, setDisplayError] = useState(error || '');
  const [textareaId] = useState(() => id || randomId());

  useEffect(() => { setDisplayError(error || ''); }, [error]);
  useEffect(() => { if (autoSaveError) setDisplayError(autoSaveError); }, [autoSaveError]);

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setLength(e.target.value.length);
    setDisplayError('');
    if (autoSaveUrl) save(e.target.value);
    onChange?.(e);
  };

  const showHint = Boolean(hint) && !displayError;
  const hasMessage = Boolean(displayError) || showHint;
  const messageId = hasMessage ? `${textareaId}-message` : undefined;
  const countId = showCharacterCount && maxLength ? `${textareaId}-count` : undefined;
  const describedBy = [messageId, countId].filter(Boolean).join(' ') || undefined;

  return (
    <div className={classNames(styles.container, className)}>
      {label && (
        <label htmlFor={textareaId} className={styles.label}>
          {label}
          {required && <span className={styles.required} aria-hidden="true">&nbsp;*</span>}
        </label>
      )}

      <div
        className={classNames(styles.textareaWrapper, styles[context], {
          [styles.error]: Boolean(displayError),
          [styles.disabled]: disabled,
        })}
      >
        <textarea
          ref={textareaRef}
          id={textareaId}
          className={styles.textarea}
          defaultValue={defaultValue}
          disabled={disabled}
          maxLength={maxLength}
          name={name}
          onChange={handleChange}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          required={required}
          rows={rows}
          aria-describedby={describedBy}
          aria-invalid={Boolean(displayError)}
        />

        {showCharacterCount && maxLength && (
          <div id={countId} className={styles.characterCount} aria-live="polite">
            {length} of {maxLength} characters
          </div>
        )}
      </div>

      {hasMessage && (
        <Hint
          id={messageId}
          variant={displayError ? 'error' : 'neutral'}
          message={displayError || hint}
        />
      )}
    </div>
  );
};

export default Textarea;
